/* eslint-disable @typescript-eslint/no-explicit-any */
import i18n from '@/i18n'
import { until, useLocalStorage } from '@vueuse/core'
import { apiService } from '@/services'
import { TRUVID_TIME_ZONES } from '@/services/constants'
import { OrganizationTypes, UserRolesNames } from '@/services/organization-user/constants'
import { getDateRanges, getDateStringReports, transfromReportsRows } from '@/services/utils'
import { computed, onMounted, reactive, ref, type ComputedRef } from 'vue'
import { DateRangeNames, SortOrder, ReportType } from '@/interfaces'
import { useReportType } from '@/composables'
import { MenuReportColumn, TableColumn } from '@/services/report/types'
import { ReportsTable, ReportsTableRow } from './types'
import { ReportCriteria } from '@/services/report/types'
import nativeClicksColumnsData from '@/components/reports/data/native-clicks-columns.json'
import store from '@/store'
import { ReportOrgType, UserRole } from '@/services/organization-user/types'

export enum Groups {
  Day = 'day',
  Hour = 'hour',
  Month = 'month',
}
export const groups = Object.values(Groups)
export const REPORTS_MAX_RESULTS = 500
export const timezones = ref(TRUVID_TIME_ZONES.map((zone) => zone.name))

const reportsStorage = reactive(
  useLocalStorage('reports-config', {
    groupBy: '' as Groups | 'none',
    timezone: '',
    dates: [] as string[],
    columns: {} as Record<string, MenuReportColumn[]> // selectedColumns
  })
)
const currentReport = ref('')
const groupByInternal = computed(() => {
  const stored = reportsStorage.value.groupBy
  if (stored == 'none') { // Previusly we had 'none' as a value for groupBy
    return Groups.Day
  } else if (stored) {
    return stored
  }
  return Groups.Day
})
const { reportType } = useReportType()
const columns = ref<MenuReportColumn[] | TableColumn[]>([])
const filtersInternal = ref([])
const dateTimeColumn = ref<MenuReportColumn | TableColumn | null>(null)
const columnsTmp = ref<MenuReportColumn[]>([])
const defaultSort = 'datetime'
const columnsGrid = ref('')
const datesInternal = ref(reportsStorage.value.dates.length ? reportsStorage.value.dates : getDateRanges(DateRangeNames.Last30days))
const submenu = ref('')
const columnsSearchText = ref('')
const oldCriteria = ref('')
const canCopy = ref(true)
const table = ref<ReportsTable>({
  columns: [],
  rows: [],
  totalRecordCount: '0',
  isLoading: false,
  isFilterLoading: false,
  sortable: {
    order: '',
    sort: ''
  },
  messages: {
    pagingInfo: '',
    pageSizeChangeLabel: '',
    gotoPageLabel: '',
    noDataAvailable: ''
  },
  totals: []
})
const allReports = [
  ReportType.console_publisher_truvid_report,
  ReportType.console_publisher_content_report,
  // ReportType.console_publisher_native_clicks_report,
  ReportType.console_content_content_report,
  ReportType.console_accounts_account_report,
  ReportType.console_engagement_report
]
const NATIVE_CLICKS_REPORT = ReportType.console_publisher_native_clicks_report
const sortInternal = ref({ sort_direction: SortOrder.Ascending, sort_field: defaultSort })
const wasDataLoaded = ref(false)
const isFetchingInitialData = ref(false)
const reportNames = ref([] as string[])
const userTimezone = computed(() => store.getters['user/getUserTimezone']())
const roles = computed(() => store.getters['user/getRoles']())
const orgTypes: ComputedRef<ReportOrgType[]> = computed(() => store.getters['user/getOrganizationType'])
const groupBy = ref<Groups>(groupByInternal.value)
const criteria = ref<ReportCriteria>({
  report_name: currentReport.value,
  start_date: '',
  end_date: '',
  columns: <string[]>[],
  include_totals: true,
  group_by: groupBy.value,
  timezone: '',
  sort_direction: SortOrder.Ascending,
  sort_field: defaultSort,
  filters: []
})
const translatedReportNamesOptions = computed(() => {
  return reportNames.value.map((key) => ({
    key,
    label: i18n.global.t(key)
  }))
})
const isSelectAll = computed({
  get: () => {
    return [
      {
        id: i18n.global.t('select-all'),
        label: i18n.global.t('select-all'),
        selected: columnsTmp.value.every((e) => e.is_selected),
        disabled: false
      }
    ]
  },
  set: (value) => {
    if (value[0].selected) {
      selectAll()
    } else {
      selectNone()
    }
  }
})

const someSearchable = computed(() => {
  return table.value.columns.some((c) => c.searchable)
})

const selectedTmpColumns = computed(() => {
  return columnsTmp.value.filter((column) => column.is_selected)
})

const selectedColumns = computed(() => {
  return columns.value.filter((column) => column.is_selected)
})

const defaultColumns = computed(() => {
  return selectedTmpColumns.value.filter((column) => column.is_default)
})

const initFilters = computed(() => {
  return {
    groupBy: Groups.Day,
    timezone: userTimezone.value,
    dates: getDateRanges(DateRangeNames.Last30days),
    columns: defaultColumns.value
  }
})

const isOnlyDefaultColumns = computed(() => selectedTmpColumns.value.every((column) => column.is_default))

const noTmpColumnSelected = computed(() => {
  return !columnsTmp.value.filter((c) => !c.is_default).some((c) => c.is_selected)
})

const isColumnsEqualToTmpColumns = computed(() => {
  return JSON.stringify(selectedColumns.value) === JSON.stringify(selectedTmpColumns.value)
})

const isNoColumnsSelected = computed(() => {
  return selectedTmpColumns.value.length === 1 && selectedTmpColumns.value[0].field === defaultSort
})

const isTempColumnsEqualToStorageColumns = computed(() => {
  return JSON.stringify(selectedTmpColumns.value) === JSON.stringify(reportsStorage.value.columns[currentReport.value])
})

const dates = computed({
  get: () => {
    return datesInternal.value
  },
  set: async (val) => {
    datesInternal.value = val
  }
})
const columnsChanged = computed(() => {
  return JSON.stringify(columns.value) !== JSON.stringify(columnsTmp.value)
})

const timezone = computed({
  get: () => {
    return reportsStorage.value.timezone || userTimezone.value
  },
  set: (value: string) => {
    reportsStorage.value.timezone = value
  }
})

const isUsingCustomTimezone = computed(() => {
  return !!reportsStorage.value.timezone && reportsStorage.value.timezone !== userTimezone.value
})

const sort = computed({
  get() {
    return sortInternal.value
  },
  set(value: any) {
    sortInternal.value.sort_direction = value.sort_direction
    sortInternal.value.sort_field = value.sort_field
    getData()
  }
})

const filters = computed({
  get() {
    return filtersInternal.value
  },
  set(value: any) {
    filtersInternal.value = value
    if (!table.value.isLoading) {
      getData()
    }
  }
})

async function getTransformedColumns(reportName: string): Promise<MenuReportColumn[] | TableColumn[]> {
  try {
    let rawCols: MenuReportColumn[] | TableColumn[] = []
    if (reportName === NATIVE_CLICKS_REPORT) {
      rawCols = nativeClicksColumnsData.columns.map((col) => ({
        field: col.column_name,
        headerName: col.title,
        orderable: col.orderable,
        is_dimension: col.is_dimension,
        is_default: col.is_default,
        searchable: col.searchable,
        is_selected: col.is_default
      }))
    } else {
      rawCols = await apiService.report.getReportColumns(reportName)
    }
    dateTimeColumn.value = rawCols.find((item) => item.field === 'datetime') || null
    if (reportsStorage.value.columns[reportName] && reportsStorage.value.columns[reportName].length) {
      const storageSelectedNames = reportsStorage.value.columns[reportName].map((c) => c.headerName)
      return rawCols.map((col) => ({
        ...col,
        is_selected: storageSelectedNames.includes(col.headerName)
      }))
    }
    return rawCols
  } catch (error) {
    console.error('Error fetching report columns:', error)
    return []
  }
}

function selectAll() {
  columnsTmp.value.forEach((element) => {
    element.is_selected = true
  })
  columnsSearchText.value = ''
}

function selectNone() {
  columnsTmp.value.forEach((element) => {
    element.is_selected = element.field === defaultSort
  })
  columnsSearchText.value = ''
}

function formatReportData(data: ReportsTableRow[]) {
  data.forEach((d) => {
    d.datetime = getDateStringReports(d.datetime, groupBy.value)
  })
  return data
}

async function getData() {
  if (!columns.value.length) return
  setFilters()
  const isInColumns = []
  filters.value?.forEach((f, index) => {
    isInColumns.push(false)
    criteria.value.columns?.forEach((c) => {
      if (c === f.column_name) {
        isInColumns[index] = true
      }
    })
  })

  criteria.value.filters = filters.value.filter((f, index) => {
    table.value.isLoading = false
    return isInColumns[index] && f.value_to_search?.length > 0
  })
  criteria.value.start_date = dates.value[0]
  criteria.value.end_date = dates.value[1]
  if (oldCriteria.value != JSON.stringify(criteria.value) + currentReport.value || table.value.rows.length === 0) {
    table.value.columns = columns.value.filter((c) => {
      table.value.isLoading = false
      return c.is_selected && (c.field !== 'datetime')
    })

    if (dateTimeColumn.value && table.value.columns.length > 0 && table.value.columns[0].field !== dateTimeColumn.value.field) {
      table.value.columns.unshift(dateTimeColumn.value)
    }
    criteria.value.max_results = REPORTS_MAX_RESULTS
    table.value.rows = []
    table.value.totals = []
    try {
      const reportRes = await apiService.report.getReports(criteria.value, currentReport.value)
      const rows2 = transfromReportsRows(reportRes)
      table.value.isFilterLoading = false
      table.value.rows = formatReportData(rows2)
      if (reportRes.totals) {
        table.value.totals = reportRes.totals[0]
      }
      filters.value.length = 0
      criteria.value.filters.forEach((f) => {
        filters.value.push(f)
      })
      columnsGrid.value = ''
      columns.value.forEach((c) => {
        if (c.is_selected) columnsGrid.value += '1fr '
      })
      oldCriteria.value = JSON.stringify(criteria.value) + currentReport.value
      table.value.isLoading = false
    } catch (error) {
      console.error('Error fetching report data:', error)
      table.value.isLoading = false
    }
  } else {
    table.value.isFilterLoading = false
  }
}

function setFilters() {
  table.value.isFilterLoading = true
  const columnsReq = columns.value
    .filter((c) => {
      return c.is_selected
    })
    .map((r) => {
      return r.field
    })
  criteria.value = {
    report_name: currentReport.value === NATIVE_CLICKS_REPORT ? ReportType.console_publisher_truvid_report : currentReport.value,
    start_date: dates.value[0],
    end_date: dates.value[1],
    columns: columnsReq,
    include_totals: true,
    group_by: groupBy.value,
    timezone: timezone.value,
    sort_field: sortInternal.value.sort_field,
    sort_direction: sortInternal.value.sort_direction,
    filters: filters.value
  }
  if (dateTimeColumn.value && criteria.value.columns && criteria.value.columns.length > 0 && !criteria.value.columns.includes(dateTimeColumn.value.field)) {
    criteria.value.columns.unshift(dateTimeColumn.value.field)
  }
}

function getColumns() {
  if (columnsSearchText.value.length > 1) {
    return columnsTmp.value.filter((c) => {
      return i18n.global.t(c.headerName).toLowerCase().indexOf(columnsSearchText.value.toLowerCase()) > -1 && c.field !== defaultSort
    })
  } else {
    return columnsTmp.value.filter((c) => {
      return c.field !== defaultSort
    })
  }
}

const columnsCheckBoxes = computed({
  get: () => {
    return getColumns().map((c) => {
      return {
        id: c.headerName,
        label: i18n.global.t(c.headerName),
        selected: c.is_selected,
        disabled: false
      }
    })
  },
  set: (val: any) => {
    columnsTmp.value.forEach((c) => {
      const newc = val.find((f) => {
        return f.id === c.headerName
      })
      if (newc) {
        c.is_selected = newc.selected
      }
    })
  }
})

function hasRole(section: OrganizationTypes, names: UserRolesNames[]) {
  return roles.value.some((role: UserRole) => role.section === section && names.includes(role.name))
}

export function clearReportsCache() {
  reportNames.value = []
  currentReport.value = ''
  wasDataLoaded.value = false
  isFetchingInitialData.value = false
}

export function useReportsMenu() {
  function getSearchableReports() {
    return table.value.columns.filter((c) => c.searchable)
  }

  function getReportNames() {
    if (reportNames.value.length === 0) {
      if (!roles.value || !orgTypes.value) {
        return []
      }
      const isAdmin = roles.value.some((role: UserRole) => role.name === UserRolesNames.OrganizationAdmin)
      const allowed = new Set<ReportOrgType>()
      const roleMappings: Record<ReportOrgType, UserRolesNames[]> = {
        [OrganizationTypes.Publisher]: [UserRolesNames.PublisherOwner, UserRolesNames.PublisherAccounting],
        [OrganizationTypes.Content]: [UserRolesNames.ContentOwner, UserRolesNames.ContentAccounting],
        [OrganizationTypes.Agency]: [UserRolesNames.AgencyOwner]
      }
      Object.entries(roleMappings).forEach(([orgType, requiredRoles]) => {
        const hasRequiredRole = hasRole(orgType as ReportOrgType, requiredRoles)
        const isAdminForOrg = isAdmin && orgTypes.value.includes(orgType as ReportOrgType)
        if (hasRequiredRole || isAdminForOrg) {
          allowed.add(orgType as ReportOrgType)
        }
      })

      const prefixBySection = {
        [OrganizationTypes.Publisher]: 'console_publisher_',
        [OrganizationTypes.Content]: 'console_content_',
        [OrganizationTypes.Agency]: 'console_accounts_'
      } as const

      const prefixes = Array.from(allowed).map((s) => prefixBySection[s])
      const result = prefixes.length ? allReports.filter((r) => prefixes.some((p) => r.includes(p))) : []

      // Add engagement report if user has any report access
      if (result.length > 0 && !result.includes(ReportType.console_engagement_report)) {
        result.push(ReportType.console_engagement_report)
      }

      reportNames.value = result
    }

    return reportNames.value
  }

  function selectDefault() {
    columnsTmp.value.forEach((element) => {
      element.is_selected = element.is_default
    })
    columnsSearchText.value = ''
  }

  async function initColumnsAndData(reportName: string) {
    if (!reportName) {
      return
    }
    columns.value = await getTransformedColumns(reportName, groupBy.value)
    table.value.columns = columns.value.filter((col) => col.is_selected)
    setColumnsTmp()
    await getData()
  }

  onMounted(async () => {
    if (wasDataLoaded.value || isFetchingInitialData.value) return
    isFetchingInitialData.value = true
    getReportNames()
    const resolvedReport = reportType.value || reportNames.value[0]
    if (!resolvedReport) {
      console.warn('[ReportsMenu] No report name found')
      isFetchingInitialData.value = false
      return
    }
    currentReport.value = resolvedReport
    if (!columns.value.length || !table.value.rows.length) {
      table.value.isLoading = true
      await initColumnsAndData(currentReport.value)
      wasDataLoaded.value = true
    }
    isFetchingInitialData.value = false
  })

  function setColumnsTmp() {
    columnsTmp.value = JSON.parse(JSON.stringify(columns.value))
  }

  function applyColumns() {
    columns.value = JSON.parse(JSON.stringify(columnsTmp.value))
  }

  async function selectReport(report: string) {
    currentReport.value = report
    sortInternal.value.sort_field = defaultSort
    table.value.columns = []
    table.value.rows = []
    table.value.isLoading = true

    await initColumnsAndData(report)

    await until(() => defaultColumns.value.length > 0 && columnsTmp.value.length > 0).toBeTruthy()
  }

  async function apply() {
    await getData()
  }

  function setSearchFilter(searchTerm, column) {
    filters.value = [{ column_name: column, value_to_search: searchTerm }]
  }

  async function downloadReportCSV() {
    const isInColumns = []
    filters.value.forEach((f, index) => {
      isInColumns.push(false)
      criteria.value.columns.forEach((c) => {
        if (c === f.column_name) {
          isInColumns[index] = true
        }
      })
    })

    criteria.value.filters = filters.value.filter((f, index) => isInColumns[index])
    criteria.value.start_date = dates.value[0]
    criteria.value.end_date = dates.value[1]
    if (canCopy.value) {
      canCopy.value = false
      window.location.assign(await apiService.report.getReportsCSV(criteria.value, currentReport.value))
      setTimeout(() => {
        canCopy.value = true
      }, 2500)
    }
  }

  function applyResetFilters() {
    groupBy.value = initFilters.value.groupBy as Groups
    reportsStorage.value.timezone = ''
    timezone.value = initFilters.value.timezone
    dates.value = initFilters.value.dates
    selectDefault() // reset columns
    applyColumns()
    apply()
  }

  return {
    currentReport,
    groupBy,
    groups,
    dates,
    reportNames,
    translatedReportNamesOptions,
    criteria,
    timezones,
    downloadReportCSV,
    table,
    filters,
    sort,
    selectReport,
    timezone,
    isUsingCustomTimezone,
    setFilters,
    columns,
    apply,
    getSearchableReports,
    someSearchable,
    submenu,
    columnsSearchText,
    setSearchFilter,
    selectAll,
    isSelectAll,
    selectDefault,
    columnsCheckBoxes,
    columnsChanged,
    columnsTmp,
    applyColumns,
    setColumnsTmp,
    noTmpColumnSelected,
    initFilters,
    applyResetFilters,
    selectedTmpColumns,
    isOnlyDefaultColumns,
    reportsStorage,
    defaultColumns,
    selectedColumns,
    isColumnsEqualToTmpColumns,
    isTempColumnsEqualToStorageColumns,
    isNoColumnsSelected
  }
}